import { createContext, useContext, useEffect, useState } from "react";
import api, { fmtError, clearApiCache } from "./api";
import { clearSWRCache } from "./useSWR";

const AuthCtx = createContext(null);

// Stale-while-revalidate cache key. We persist the last-known-good `user`
// object alongside the JWT so the next page load can hydrate the UI
// SYNCHRONOUSLY — no flicker, no "Loading…" splash — and refresh the truth
// in the background.
const USER_CACHE_KEY = "pollpak_user_cache";

function readCachedUser() {
  try {
    const token = localStorage.getItem("pollpak_token");
    if (!token) return null;
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" && obj.id ? obj : null;
  } catch {
    return null;
  }
}

function writeCachedUser(u) {
  try {
    if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch { /* quota / disabled storage — non-fatal */ }
}

export function AuthProvider({ children }) {
  // Hydrate synchronously from the cache so the very first render already
  // has `user` populated. `loading` is true ONLY when we have no cached user
  // (genuine cold start) — otherwise we render instantly and refresh silently.
  const cached = typeof window !== "undefined" ? readCachedUser() : null;
  const [user, setUserState] = useState(cached);
  const [loading, setLoading] = useState(!cached && !!(typeof window !== "undefined" && localStorage.getItem("pollpak_token")));
  const [loggingOut, setLoggingOut] = useState(false);

  // Setter that also persists to the cache (kept in sync automatically).
  const setUser = (u) => { setUserState(u); writeCachedUser(u); };

  useEffect(() => {
    const token = localStorage.getItem("pollpak_token");
    if (!token) { setLoading(false); return; }
    // Background revalidate — even if we hydrated from cache, refresh in
    // case the user's role/permissions/tier changed server-side.
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("pollpak_token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("pollpak_token", data.token);
      setUser(data.user || data);
      return data.user || data;
    } catch (e) {
      throw new Error(fmtError(e.response?.data?.detail) || e.message);
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, name });
      return data;
    } catch (e) {
      throw new Error(fmtError(e.response?.data?.detail) || e.message);
    }
  };

  const logout = () => {
    setLoggingOut(true);
    // If the admin is currently impersonating, accidentally hitting Logout
    // would just kill the impersonation token but leave the red banner
    // visible (because its source-of-truth lives in sessionStorage). To
    // avoid the orphaned-banner footgun we wipe ALL impersonation state
    // here too — the admin can simply log back in to resume their own work.
    try {
      sessionStorage.removeItem("pollpak_impersonation_target");
      sessionStorage.removeItem("pollpak_admin_token");
      sessionStorage.removeItem("pollpak_admin_user_cache");
    } catch { /* quota */ }
    // Clear ONLY user-specific state. The CMS site-content cache is NOT
    // user-specific (it's the same labels for every user) — keeping it
    // across logout/login means the next login paints the admin's current
    // labels on the FIRST frame instead of flashing the hardcoded fallback.
    localStorage.removeItem("pollpak_token");
    try {
      localStorage.removeItem("oodel_panelist_cms_cache_v1");
      localStorage.removeItem("oodel_admin_menu_cache_v1");
      localStorage.removeItem("pollpak_user_cache");
    } catch { /* quota */ }
    clearApiCache();
    setUser(null);
    setTimeout(() => setLoggingOut(false), 600);
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, loggingOut, login, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
